import { useRef } from 'react';
import { FaPlus } from 'react-icons/fa';
import { Dialog } from '../ui/dialog';
import ConfirmDeleteModal from '../admin/ConfirmDeleteModal';
import LinhaItemManutencao from './LinhaItemManutencao';
import useTabelaCrudInline from '../../hooks/useTabelaCrudInline';
import useToast from '../../hooks/useToast';
import { PERIODICIDADES, validarItem } from './periodicidades';

const LINHA_VAZIA = () => ({
    acao: '',
    periodicidade: 'Diaria',
    diaAviso: '',
    mesAviso: '',
    dataProgramada: '',
});

const numeroOuNulo = (valor) => (valor === '' || valor == null ? null : parseInt(valor, 10));

export default function TabelaItensManutencao({ tabela, podeEditar, onCriar, onEditar, onExcluir }) {
    const { erro } = useToast();
    const valoresRef = useRef(null);

    const comValidacao = (acao) => async (...args) => {
        const mensagem = validarItem(valoresRef.current);
        if (mensagem) {
            erro(mensagem);
            return false;
        }
        return acao(...args);
    };

    const {
        NOVO_ID,
        linhaEmEdicaoId,
        valores,
        onChangeValor,
        itemRemovido,
        salvando,
        itensPaginados: itens,
        iniciarEdicao,
        iniciarNovo,
        cancelar,
        salvar,
        selecionarParaExcluir,
        confirmarExclusao,
    } = useTabelaCrudInline({
        itens: tabela.itens,
        linhaVazia: LINHA_VAZIA,
        valoresParaEdicao: (item) => ({
            acao: item.acao,
            periodicidade: item.periodicidade,
            diaAviso: item.diaAviso ?? '',
            mesAviso: item.mesAviso ?? '',
            dataProgramada: item.dataProgramada ?? '',
        }),
        montarPayload: (valores) => ({
            tabelaManutencaoId: tabela.id,
            acao: valores.acao,
            periodicidade: valores.periodicidade,
            diaAviso: ['Semanal', 'Mensal', 'Semestral', 'Anual'].includes(valores.periodicidade) ? numeroOuNulo(valores.diaAviso) : null,
            mesAviso: ['Semestral', 'Anual'].includes(valores.periodicidade) ? numeroOuNulo(valores.mesAviso) : null,
            dataProgramada: valores.periodicidade === 'Programada' && valores.dataProgramada ? valores.dataProgramada : null,
        }),
        nomeParaExclusao: (item) => item.acao,
        onCriar: comValidacao(onCriar),
        onEditar: comValidacao(onEditar),
        onExcluir,
        itensPorPagina: Number.MAX_SAFE_INTEGER,
    });

    valoresRef.current = valores;

    return (
        <Dialog>
            <div className="w-full overflow-x-auto">
                <table className="w-full table-fixed text-white min-w-[960px]">
                    <thead>
                        <tr className="text-left border-b border-gray-700">
                            <th className="p-2 text-lg w-[38%]">Ação</th>
                            {PERIODICIDADES.map((p) => (
                                <th key={p.valor} className="p-2 text-lg text-center">{p.nome}</th>
                            ))}
                            {podeEditar && <th className="p-2 text-lg text-center w-[10%]">Opções</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {itens.length === 0 && linhaEmEdicaoId !== NOVO_ID && (
                            <tr>
                                <td colSpan={PERIODICIDADES.length + 2} className="p-3 text-center text-lg text-gray-400">
                                    Nenhuma ação cadastrada nesta tabela.
                                </td>
                            </tr>
                        )}
                        {itens.map((item) => (
                            <LinhaItemManutencao
                                key={item.id}
                                item={item}
                                podeEditar={podeEditar}
                                emEdicao={linhaEmEdicaoId === item.id}
                                bloqueado={linhaEmEdicaoId !== null && linhaEmEdicaoId !== item.id}
                                salvando={salvando}
                                valores={valores}
                                onChangeValor={onChangeValor}
                                onEditar={iniciarEdicao}
                                onCancelar={cancelar}
                                onSalvar={salvar}
                                onExcluir={selecionarParaExcluir}
                            />
                        ))}
                        {linhaEmEdicaoId === NOVO_ID && (
                            <LinhaItemManutencao
                                item={LINHA_VAZIA()}
                                podeEditar={podeEditar}
                                emEdicao={true}
                                salvando={salvando}
                                valores={valores}
                                onChangeValor={onChangeValor}
                                onCancelar={cancelar}
                                onSalvar={salvar}
                            />
                        )}
                    </tbody>
                </table>
            </div>
            {podeEditar && (
                <div className="flex justify-end p-2">
                    <button
                        type="button"
                        onClick={iniciarNovo}
                        disabled={linhaEmEdicaoId !== null}
                        className="flex items-center gap-2 bg-accent text-white px-3 py-1.5 rounded-md text-lg disabled:opacity-50"
                    >
                        <FaPlus /> Adicionar ação
                    </button>
                </div>
            )}
            <ConfirmDeleteModal itemNome={itemRemovido} onConfirm={confirmarExclusao} />
        </Dialog>
    );
}
